import { useState, useEffect, useRef } from 'react';

const formatTime = (totalSeconds) => {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) return '--';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
};

const resolveStartMs = (startedAt, fallback) => {
  const tryMillis = startedAt?.toMillis?.();
  if (Number.isFinite(tryMillis)) return tryMillis;
  const trySeconds = startedAt?.seconds;
  if (Number.isFinite(trySeconds)) return trySeconds * 1000;
  return fallback;
};

const useElapsedTime = (startedAt, overallProgress, backendEstimate) => {
  const localStartRef = useRef(Date.now());
  const emaRef = useRef(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [estimatedRemainingSeconds, setEstimatedRemainingSeconds] = useState(null);

  const startMs = resolveStartMs(startedAt, localStartRef.current);

  // Reset EMA when the start time changes (e.g. resume) so we don't blend
  // stale samples into a fresh run.
  useEffect(() => { emaRef.current = null; }, [startMs]);

  // Tick once per second. Both elapsed and ETA update here — putting the EMA
  // blend inside the tick means it advances once per real time step instead
  // of re-blending on every render (which would collapse the 70/30 smoothing).
  useEffect(() => {
    const tick = () => {
      const elapsed = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
      setElapsedSeconds(elapsed);

      let nextRemaining = null;
      if (overallProgress > 10 && elapsed > 5) {
        const raw = (elapsed / overallProgress) * (100 - overallProgress);
        if (emaRef.current === null) {
          emaRef.current = raw;
        } else {
          emaRef.current = emaRef.current * 0.7 + raw * 0.3;
        }
        nextRemaining = Math.max(5, Math.min(300, Math.round(emaRef.current)));
      } else if (Number.isFinite(backendEstimate) && backendEstimate > 0) {
        nextRemaining = backendEstimate;
      }
      if (overallProgress >= 95) {
        nextRemaining = nextRemaining != null ? Math.min(nextRemaining, 10) : 5;
      }
      setEstimatedRemainingSeconds(nextRemaining);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startMs, overallProgress, backendEstimate]);

  return {
    elapsedSeconds,
    estimatedRemainingSeconds,
    formattedElapsed: formatTime(elapsedSeconds),
    formattedRemaining: estimatedRemainingSeconds != null
      ? `~${formatTime(estimatedRemainingSeconds)}`
      : 'Estimating...',
  };
};

export default useElapsedTime;
